import React, { useState, useEffect, useCallback } from 'react';
import { matchesAPI } from '../services/api';
import { Match } from '../types';
import MatchCard from '../components/common/MatchCard';
import AnalysisModal from '../components/analysis/AnalysisModal';
import { useAuthStore } from '../store';
import { FaSync, FaCalendarAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';

const SPORTS = [
  { id: '', label: 'Todos', icon: '🏆' },
  { id: 'football', label: 'Fútbol', icon: '⚽' },
  { id: 'basketball', label: 'Baloncesto', icon: '🏀' },
  { id: 'american_football', label: 'NFL', icon: '🏈' },
  { id: 'baseball', label: 'Béisbol', icon: '⚾' },
  { id: 'hockey', label: 'Hockey', icon: '🏒' },
];

// Generate date strip: today + 7 days
const generateDateStrip = () => {
  const days: { label: string; iso: string }[] = [];
  const today = new Date();
  for (let i = 0; i <= 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const iso = d.toISOString().split('T')[0];
    let label = '';
    if (i === 0) label = 'Hoy';
    else if (i === 1) label = 'Mañana';
    else label = d.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric' });
    days.push({ label, iso });
  }
  return days;
};

const dateStrip = generateDateStrip();

const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [selectedSport, setSelectedSport] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDateIdx, setSelectedDateIdx] = useState(0);

  const loadMatches = useCallback(async (showToast = false) => {
    try {
      const params: any = {};
      if (selectedSport) params.deporte = selectedSport;

      let res;
      if (selectedDateIdx === 0) {
        res = await matchesAPI.getToday(params);
        setMatches(res.data.matches || []);
      } else {
        res = await matchesAPI.getByDate(dateStrip[selectedDateIdx].iso, params);
        setMatches(Array.isArray(res.data) ? res.data : []);
      }

      if (showToast) toast.success('Partidos actualizados');
    } catch {
      toast.error('Error al cargar partidos');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedSport, selectedDateIdx]);

  useEffect(() => {
    setLoading(true);
    loadMatches();
  }, [loadMatches]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadMatches(true);
  };

  const filteredMatches = matches.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return m.local_nombre?.toLowerCase().includes(q) ||
      m.visitante_nombre?.toLowerCase().includes(q) ||
      m.liga?.toLowerCase().includes(q);
  });

  const liveMatches = filteredMatches.filter(m => m.estado === 'LIVE');
  const otherMatches = filteredMatches.filter(m => m.estado !== 'LIVE');
  const liveCount = matches.filter(m => m.estado === 'LIVE').length;

  return (
    <div className="min-h-screen bg-slate-950 pt-20 px-4 pb-8">
      <div className="max-w-7xl mx-auto">

        {/* Compact header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-white">
              Hola, <span className="text-indigo-400">{user?.nombre?.split(' ')[0]}</span>
            </h1>
            <p className="text-slate-400 text-xs mt-0.5">
              {matches.length} partidos · {dateStrip[selectedDateIdx].label}
              {liveCount > 0 && <span className="ml-2 text-red-400 font-semibold">· {liveCount} en vivo</span>}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center gap-1.5 text-xs py-1.5 px-3"
          >
            <FaSync className={refreshing ? 'animate-spin' : ''} size={11} />
            Actualizar
          </button>
        </div>

        {/* Date strip + filters in one card */}
        <div className="card mb-5">
          {/* Date selector */}
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-700">
            <FaCalendarAlt className="text-indigo-400 flex-shrink-0" size={13} />
            <div className="flex gap-1.5 overflow-x-auto flex-1" style={{ scrollbarWidth: 'none' }}>
              {dateStrip.map((day, idx) => (
                <button
                  key={day.iso}
                  onClick={() => setSelectedDateIdx(idx)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                    selectedDateIdx === idx
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/40'
                      : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sport + search filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-40">
              <input
                type="text"
                placeholder="Buscar equipo o liga..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field text-sm py-1.5"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {SPORTS.map(sport => (
                <button
                  key={sport.id}
                  onClick={() => setSelectedSport(sport.id)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedSport === sport.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  <span>{sport.icon}</span>
                  <span className="hidden sm:inline">{sport.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Matches Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-4 bg-slate-700 rounded mb-3 w-3/4" />
                <div className="flex justify-between py-3">
                  <div className="h-10 w-10 bg-slate-700 rounded-full" />
                  <div className="h-8 w-12 bg-slate-700 rounded" />
                  <div className="h-10 w-10 bg-slate-700 rounded-full" />
                </div>
                <div className="h-2 bg-slate-700 rounded mt-3" />
              </div>
            ))}
          </div>
        ) : filteredMatches.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-xl font-semibold text-slate-300">No hay partidos disponibles</h3>
            <p className="text-slate-500 mt-2">
              {searchQuery
                ? 'No se encontraron partidos con ese criterio'
                : `No hay partidos para ${dateStrip[selectedDateIdx].label.toLowerCase()}`}
            </p>
          </div>
        ) : (
          <>
            {/* Live Section */}
            {liveMatches.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-red-400 flex items-center gap-2 mb-3">
                  <div className="live-dot" />
                  EN VIVO ({liveMatches.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {liveMatches.map(match => (
                    <MatchCard key={match.id} match={match} onAnalyze={setSelectedMatch} />
                  ))}
                </div>
              </div>
            )}

            {/* Scheduled Section */}
            {otherMatches.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-slate-400 mb-3 uppercase">
                  {dateStrip[selectedDateIdx].label} ({otherMatches.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {otherMatches.map(match => (
                    <MatchCard key={match.id} match={match} onAnalyze={setSelectedMatch} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedMatch && (
        <AnalysisModal match={selectedMatch} onClose={() => setSelectedMatch(null)} />
      )}
    </div>
  );
};

export default Dashboard;
